import * as Yup from 'yup';
import { Shape } from '../yup_types';
import { IDisputeQueueItem } from '../../store/tables/disputes/disputes-queue';
import { checkIfValidUUID } from '../../layout/core';

export enum DISPUTE_FORM_FIELDS {
	DISPUTE_TYPE = 'disputeType',
	TRANSACTION_ID = 'transactionId',
	REQUEST_DATE = 'requestDate',
	BIN = 'bin',
	LAST_4 = 'last4',
	CASE = 'caseNumber',
	MANAGER_SYSTEM = 'disputeManagementSystem',
	IS_REFUND = 'issueRefund',
	CARD_TYPE = 'cardBrand',
	DESCRIPTOR = 'descriptor'
}

export enum DISPUTE_FORM_FIELDS_NAMES {
	DISPUTE_TYPE = 'Dispute Type',
	TRANSACTION_ID = 'Transaction Id',
	REQUEST_DATE = 'Request Date',
	BIN = 'Bin',
	LAST_4 = 'Last4',
	CASE = 'Case #',
	MANAGER_SYSTEM = 'Dispute management system',
	IS_REFUND = 'Issue a refund?',
	CARD_TYPE = 'Card brand',
	DESCRIPTOR = 'Descriptor'
}

export type DisputeFieldsTypes = {
	[DISPUTE_FORM_FIELDS.DISPUTE_TYPE]: number | string,
	[DISPUTE_FORM_FIELDS.TRANSACTION_ID]: string,
	[DISPUTE_FORM_FIELDS.REQUEST_DATE]: string | Date | null,
	[DISPUTE_FORM_FIELDS.BIN]: string | number,
	[DISPUTE_FORM_FIELDS.LAST_4]: string | number,
	[DISPUTE_FORM_FIELDS.CASE]: string,
	[DISPUTE_FORM_FIELDS.MANAGER_SYSTEM]: string,
	[DISPUTE_FORM_FIELDS.IS_REFUND]: boolean,
	[DISPUTE_FORM_FIELDS.CARD_TYPE]: string,
	[DISPUTE_FORM_FIELDS.DESCRIPTOR]: string,
}

export type DisputeFieldsReturnTypes = {
	disputeType: number | string,
	transactionId: string,
	requestDate: string | Date | null,
	bin: string | number,
	last4: string | number,
	caseNumber: string,
	disputeManagementSystem: string,
	cardBrand: string,
	descriptor: string,
	issueRefund?: boolean,
	orderId?: string | number,
}

// @ts-ignore
export const getDisputeDetailSchema = (): Yup.SchemaOf<DisputeFieldsTypes> => Yup.object<Shape<DisputeFieldsTypes>>().shape({
	[DISPUTE_FORM_FIELDS.DISPUTE_TYPE]: Yup.number()
		.typeError('Please, enter valid Card type')
		.required('Dispute type is required'),
	[DISPUTE_FORM_FIELDS.TRANSACTION_ID]: Yup.string()
		.test('valid transaction id', 'Enter valid GUID id', (item) => {
			if (!item?.trim()) return true;
			return checkIfValidUUID(item);
		})
		.required('Transaction id is required'),
	[DISPUTE_FORM_FIELDS.REQUEST_DATE]: Yup.string()
		.typeError('Request Date is required')
		.required('Request Date is required'),
	[DISPUTE_FORM_FIELDS.BIN]: Yup.number()
		.test('is-six-chars', 'BIN must be exactly 6 characters', (value) => !value || String(value).length === 6),
	[DISPUTE_FORM_FIELDS.LAST_4]: Yup.number()
		.test('is-six-chars', 'Last4 must be exactly 4 characters', (value) => !value || String(value).length === 4),
	[DISPUTE_FORM_FIELDS.CASE]: Yup.string()
		.required('Case is required'),
	[DISPUTE_FORM_FIELDS.MANAGER_SYSTEM]: Yup.string()
		.typeError('Dispute management system is required')
		.required('Dispute management system is required'),
	[DISPUTE_FORM_FIELDS.CARD_TYPE]: Yup.string()
		.typeError('Card type is required')
		.required('Card type is required'),
	[DISPUTE_FORM_FIELDS.DESCRIPTOR]: Yup.string()
		.required('Description is required'),
});

export const initialDisputeValues = {
	[DISPUTE_FORM_FIELDS.DISPUTE_TYPE]: '',
	[DISPUTE_FORM_FIELDS.TRANSACTION_ID]: '',
	[DISPUTE_FORM_FIELDS.REQUEST_DATE]: null,
	[DISPUTE_FORM_FIELDS.BIN]: '',
	[DISPUTE_FORM_FIELDS.LAST_4]: '',
	[DISPUTE_FORM_FIELDS.CASE]: '',
	[DISPUTE_FORM_FIELDS.MANAGER_SYSTEM]: '',
	[DISPUTE_FORM_FIELDS.CARD_TYPE]: '',
	[DISPUTE_FORM_FIELDS.DESCRIPTOR]: '',
	[DISPUTE_FORM_FIELDS.IS_REFUND]: false,
};

export const convertToInitialDisputeValues = (data): DisputeFieldsTypes => ({
	[DISPUTE_FORM_FIELDS.DISPUTE_TYPE]: data?.disputeType,
	[DISPUTE_FORM_FIELDS.TRANSACTION_ID]: data?.transactionId,
	[DISPUTE_FORM_FIELDS.REQUEST_DATE]: data?.requestDate,
	[DISPUTE_FORM_FIELDS.BIN]: data?.bin,
	[DISPUTE_FORM_FIELDS.LAST_4]: data?.last4,
	[DISPUTE_FORM_FIELDS.CASE]: data?.caseNumber,
	[DISPUTE_FORM_FIELDS.MANAGER_SYSTEM]: data?.disputeManagementSystem,
	[DISPUTE_FORM_FIELDS.IS_REFUND]: data?.issueRefund,
	[DISPUTE_FORM_FIELDS.CARD_TYPE]: data?.cardBrand,
	[DISPUTE_FORM_FIELDS.DESCRIPTOR]: data?.descriptor,
});

export const getFormattedDisputeFormFields = (formikValues): DisputeFieldsReturnTypes => ({
	disputeType: formikValues?.[DISPUTE_FORM_FIELDS.DISPUTE_TYPE],
	transactionId: formikValues?.[DISPUTE_FORM_FIELDS.TRANSACTION_ID],
	requestDate: formikValues?.[DISPUTE_FORM_FIELDS.REQUEST_DATE],
	bin: formikValues?.[DISPUTE_FORM_FIELDS.BIN],
	last4: formikValues?.[DISPUTE_FORM_FIELDS.LAST_4],
	caseNumber: formikValues?.[DISPUTE_FORM_FIELDS.CASE],
	disputeManagementSystem: formikValues?.[DISPUTE_FORM_FIELDS.MANAGER_SYSTEM],
	issueRefund: formikValues?.[DISPUTE_FORM_FIELDS.IS_REFUND],
	cardBrand: formikValues?.[DISPUTE_FORM_FIELDS.CARD_TYPE],
	descriptor: formikValues?.[DISPUTE_FORM_FIELDS.DESCRIPTOR],
});

export enum MATCH_DISPUTE_FORM_FIELD {
	STATUS_CODE = 'KountStatusCode',
	COMMENTS = 'comments',
	AMOUNT = 'amountToRefund',
}

export enum MATCH_DISPUTE_FORM_FIELD_NAMES {
	STATUS_CODE = 'Status code',
	MY_RCVR_STATUS_CODE = 'MyRCVR status code',
	COMMENTS = 'Comments',
	AMOUNT = 'Amount',
}

export type MatchDisputesTypes = {
	[MATCH_DISPUTE_FORM_FIELD.STATUS_CODE]: string,
	[MATCH_DISPUTE_FORM_FIELD.COMMENTS]: string,
	[MATCH_DISPUTE_FORM_FIELD.AMOUNT]?: string | number,
}

export type MatchDisputesReturnTypes = {
	KountStatusCode?: string,
	comments?: string,
	amountToRefund?: string | number,
	disputeQueueItemId?: string,
	transactionId?: string,
	issueRefund?: boolean,
}

export const getAlertMatchDisputeModalSchema = (data: IDisputeQueueItem): Yup.SchemaOf<MatchDisputesTypes> => Yup.object<Shape<MatchDisputesTypes>>().shape({
	[MATCH_DISPUTE_FORM_FIELD.STATUS_CODE]: Yup.string()
		.required('Status code is required'),
	[MATCH_DISPUTE_FORM_FIELD.COMMENTS]: Yup.string()
		.max(200, 'Note must not be longer then 200 characters long'),
	[MATCH_DISPUTE_FORM_FIELD.AMOUNT]: Yup.number()
		.test('test', 'Amount must be less then dispute amount', (value) => {
			return value ? value <= data?.amount : true;
		})
});

// export const getKountMatchDisputeModalSchema = (data: IDisputeQueueItem): Yup.SchemaOf<MatchDisputesTypes> => Yup.object<Shape<MatchDisputesTypes>>().shape({
// 	[MATCH_DISPUTE_FORM_FIELD.STATUS_CODE]: Yup.string()
// 		.required('Status code is required'),
// 	[MATCH_DISPUTE_FORM_FIELD.COMMENTS]: Yup.string()
// 		.max(200, 'Note must not be longer then 200 characters long'),
// 	[MATCH_DISPUTE_FORM_FIELD.AMOUNT]: Yup.number()
// 		.test('test', 'Amount must be less then dispute amount', (value) => {
// 			return value ? value <= data?.amount : true;
// 		})
// });
//
// export const getMyRcvrMatchDisputeModalSchema = (data: IDisputeQueueItem): Yup.SchemaOf<MatchDisputesTypes> => Yup.object<Shape<MatchDisputesTypes>>().shape({
// 	[MATCH_DISPUTE_FORM_FIELD.STATUS_CODE]: Yup.string()
// 		.required('Status code is required'),
// 	[MATCH_DISPUTE_FORM_FIELD.COMMENTS]: Yup.string()
// 		.max(200, 'Note must not be longer then 200 characters long'),
// 		// .required('Comment is required'),
// 	[MATCH_DISPUTE_FORM_FIELD.AMOUNT]: Yup.number()
// 		.test('test', 'Amount must be less then dispute amount', (value) => {
// 			return value ? value <= data?.amount : true;
// 		})
// });

export const getMatchDisputeModalSchema = (data: IDisputeQueueItem): Yup.SchemaOf<MatchDisputesTypes> => Yup.object<Shape<MatchDisputesTypes>>().shape({
	[MATCH_DISPUTE_FORM_FIELD.STATUS_CODE]: Yup.string(),
		// .required('Status code is required'),
	[MATCH_DISPUTE_FORM_FIELD.COMMENTS]: Yup.string(),
		// .max(200, 'Note must not be longer then 200 characters long'),
	[MATCH_DISPUTE_FORM_FIELD.AMOUNT]: Yup.number()
		.test('test', 'Amount must be less then dispute amount', (value) => {
			return value ? value <= data?.amount : true;
		})
});
export const getAlertCountModalSchema = (data: IDisputeQueueItem): Yup.SchemaOf<MatchDisputesTypes> => Yup.object<Shape<MatchDisputesTypes>>().shape({
	[MATCH_DISPUTE_FORM_FIELD.STATUS_CODE]: Yup.string()
		.required('Status code is required'),
	[MATCH_DISPUTE_FORM_FIELD.COMMENTS]: Yup.string(),
		// .required('Comment is required'),
		// .max(200, 'Note must not be longer then 200 characters long'),
	[MATCH_DISPUTE_FORM_FIELD.AMOUNT]: Yup.number()
		.test('test', 'Amount must be less then dispute amount', (value) => {
			return value ? value <= data?.amount : true;
		})
});

export const initialMatchDisputeModalValues = {
	[MATCH_DISPUTE_FORM_FIELD.STATUS_CODE]: '',
	[MATCH_DISPUTE_FORM_FIELD.COMMENTS]: '',
	[MATCH_DISPUTE_FORM_FIELD.AMOUNT]: '',
};

export const getFormattedMatchDisputeFormFields = (formikValues): MatchDisputesReturnTypes => ({
	KountStatusCode: formikValues?.[MATCH_DISPUTE_FORM_FIELD.STATUS_CODE],
	comments: formikValues?.[MATCH_DISPUTE_FORM_FIELD.COMMENTS],
	amountToRefund: formikValues?.[MATCH_DISPUTE_FORM_FIELD.AMOUNT],
});
